import { Component } from 'react';
import PropTypes from 'prop-types';
import { Field, Form } from 'react-final-form';

import getRequiredLabel from 'src/common/utils/form/getRequiredLabel';
import {
  FORGOT_PASSWORD_SUBMIT_BUTTON,
  BACK_TO_SIGN_IN,
  EMAIL_DOES_NOT_EXIST,
  RESET_PASSWORD_IN_PROGRESS,
} from 'src/common/constants/localizations/auth';
import {
  EMAIL_ADDRESS,
  EMAIL_REQUIRED,
} from 'src/common/constants/localizations/shared';
import {
  FAULTCODE_USERNAME,
  FAULTCODE_RESET_IN_PROGRESS,
} from 'src/common/constants/faultCodes';
import Input from '../shared/form/Input';
import PillButton from '../shared/button/PillButton';
import TopLevelErrorContainer from '../shared/form/TopLevelErrorContainer';
import UnderlineLink from '../shared/link/UnderlineLink';
import { LocationAndLocalizationContext } from '../../providers/LocationAndLocalizationProvider';
import {
  baseError,
  composeValidators,
  isRequired,
  isValidEmail,
} from '../../utils/form/validation';
import NeedHelp from './shared/NeedHelp';
import GeneralNotices from './shared/GeneralNotices';
import authCss from './auth.module.scss';

export class ForgotPassword extends Component {
  static propTypes = {
    actions: PropTypes.shape({
      forgotPassword: PropTypes.func.isRequired,
    }),
    successAuthNotice: PropTypes.string,
    handleChangeToSignIn: PropTypes.func.isRequired,
  };

  static contextType = LocationAndLocalizationContext;

  onSubmit = async (data, form) => {
    const { actions } = this.props;
    const { email } = data;

    try {
      await actions.forgotPassword(email);
      form.restart();
    } catch (error) {
      const responseData = error.response?.data;
      switch (responseData?.faultCode) {
        case FAULTCODE_RESET_IN_PROGRESS:
          return baseError(RESET_PASSWORD_IN_PROGRESS);
        case FAULTCODE_USERNAME:
        default:
          return { email: [EMAIL_DOES_NOT_EXIST] };
      }
    }
  };

  render() {
    const { handleChangeToSignIn, successAuthNotice } = this.props;

    const { getLocalizedText } = this.context;

    return (
      <Form onSubmit={this.onSubmit}>
        {({
          handleSubmit,
          dirty,
          hasValidationErrors,
          submitting,
          submitErrors,
          touched,
          errors,
        }) => (
          <form name="forgotPassword" onSubmit={handleSubmit}>
            <div
              data-testid="forgotPassword"
              className={authCss.containerRestrictions}
            >
              <GeneralNotices
                key="signInGeneralNotices"
                successAuthNotice={successAuthNotice}
              />
              <TopLevelErrorContainer
                className={authCss.largeSection}
                errorMessages={
                  dirty || touched ? { ...submitErrors, ...errors } : {}
                }
                fieldOrder={['email']}
                touchedFields={touched}
              />
              <div className={authCss.section}>
                <div className={authCss.fieldSection}>
                  <div className="row">
                    <div className="col-xs-12">
                      <Field
                        component={Input}
                        dataTestId="email"
                        className="test-email"
                        name="email"
                        id="fpEmail"
                        hintText={getRequiredLabel(
                          getLocalizedText(EMAIL_ADDRESS),
                        )}
                        validate={composeValidators(
                          isRequired(EMAIL_REQUIRED),
                          isValidEmail,
                        )}
                        isRequired
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className={authCss.section}>
                <div className="row">
                  <div className="col-xs-12">
                    <PillButton
                      id="forgotPasswordSubmit"
                      type="submit"
                      label={getLocalizedText(FORGOT_PASSWORD_SUBMIT_BUTTON)}
                      isDisabled={submitting || hasValidationErrors}
                      className={authCss.forgotPasswordButton}
                    />
                  </div>
                </div>
              </div>
              <div className={authCss.section}>
                <div className="row">
                  <div className="col-xs-12">
                    <NeedHelp />
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-xs-12">
                  <UnderlineLink
                    role="button"
                    tabIndex="0"
                    data-testid="backToSignIn"
                    className={authCss.link}
                    onClick={handleChangeToSignIn}
                    isDropDown
                    isNotReactRouterLink
                  >
                    {getLocalizedText(BACK_TO_SIGN_IN)}
                  </UnderlineLink>
                </div>
              </div>
            </div>
          </form>
        )}
      </Form>
    );
  }
}

export default ForgotPassword;
